import { ChangeEvent, Dispatch, SetStateAction } from "react";
import {
  SelectItemType,
  SelectType,
  SelectWithOption,
  Values,
  Item,
} from "../InputSelect";
import { getLongMonthNames } from "../../DatePicker/utils/index";
import { DateRange } from "../../DatePicker/DatePicker";

export const flattenItemOptions = (items: SelectItemType[]) => {
  return items.reduce<Item[]>((acc, item) => {
    // Add the current item without options to the accumulator
    const itemWithoutOptions = { ...item };
    delete itemWithoutOptions.options;
    acc.push(itemWithoutOptions);

    // If the item has options, add them to the accumulator
    if (item.options) {
      item.options.forEach((option) => {
        acc.push(option);
      });
    }
    return acc;
  }, []);
};

export const selectChangeHandler = (
  e: ChangeEvent<HTMLSelectElement>,
  items: SelectItemType[],
  type: SelectType,
  values: Values,
  setValues: Dispatch<SetStateAction<Values>>,
  setIsOpen: Dispatch<SetStateAction<boolean>>,
  confirmSelection: boolean | undefined,
  setDatePickerRange?: Dispatch<SetStateAction<DateRange | undefined>>
) => {
  const targetValue = e.target.value;

  // SINGLE
  if (type.includes("single") && !type.includes("date-picker")) {
    if (targetValue !== "actions") {
      if (!(type.includes("radio") && !targetValue)) {
        setValues(targetValue);
        if (!confirmSelection) {
          setIsOpen(false);
        }
      }
    }
    return;
  }

  // DATE PICKER
  if (type.includes("date-picker")) {
    if (
      targetValue !== "pick-day" &&
      targetValue !== "pick-month" &&
      targetValue !== "date-picker"
    ) {
      setValues(targetValue);
      setDatePickerRange && setDatePickerRange([undefined, undefined]);
    }
    return;
  }

  // MULTIPLE
  let valuesArray = targetValue
    .split(",")
    .filter((val) => val !== "actions" && val !== "");
  const itemsArray = items || [];

  // Value that represents the "select all" option (puede ser "")
  const allOptionValue = itemsArray.find((item) => item.selectAll)?.value || "";

  const allRealItems = itemsArray
    .filter((item) => !item.selectAll)
    .flatMap((item) =>
      item.options
        ? [item.value, ...item.options.map((opt) => opt.value)]
        : item.value
    );

  // Get current values as array
  const currentValuesArray = Array.isArray(values) ? values : [values];

  // Flags sobre ALL
  const allWasPreviouslySelected = allOptionValue
    ? currentValuesArray.includes(allOptionValue)
    : false;
  const allIsCurrentlySelected = allOptionValue
    ? valuesArray.includes(allOptionValue)
    : false;

  // --- HANDLE "SELECT ALL" LOGIC ---
  if (!allWasPreviouslySelected && allIsCurrentlySelected) {
    // ALL was just selected - select everything (todos los items reales + el valor ALL)
    const toSet = allOptionValue
      ? [...allRealItems, allOptionValue]
      : [...allRealItems];
    return setValues(toSet);
  }

  if (allWasPreviouslySelected && !allIsCurrentlySelected) {
    const allItemsExceptAll = [...allRealItems];
    if (
      valuesArray.length === allItemsExceptAll.length &&
      allItemsExceptAll.every((item) => valuesArray.includes(item))
    ) {
      return setValues([]);
    }

    const clickedItem = currentValuesArray.find(
      (val) => val !== allOptionValue && !valuesArray.includes(val)
    );

    if (clickedItem) {
      return setValues([clickedItem]);
    } else if (valuesArray.length === 1) {
      return setValues(valuesArray);
    } else {
      valuesArray = handleValuesWithOptions(valuesArray, itemsArray);
      return setValues([...new Set(valuesArray)]);
    }
  }

  if (allWasPreviouslySelected && allIsCurrentlySelected) {
    const removed = currentValuesArray.find(
      (val) => !valuesArray.includes(val) && val !== allOptionValue
    );
    const added = valuesArray.find(
      (val) => !currentValuesArray.includes(val) && val !== allOptionValue
    );

    const clickedItem = added || removed;

    if (clickedItem) {
      return setValues([clickedItem]);
    }
  }

  valuesArray = handleValuesWithOptions(valuesArray, itemsArray);

  const allRealItemsSelected =
    allRealItems.length > 0 &&
    allRealItems.every((item) => valuesArray.includes(item));

  if (allRealItemsSelected && allOptionValue) {
    valuesArray.push(allOptionValue);
  }

  setValues([...new Set(valuesArray)]);
};

// Function to select or unselect parent and child options
const handleValuesWithOptions = (
  valuesArray: string[],
  itemsArray: SelectItemType[]
) => {
  // Filter items that have options
  const itemsWithOptions = itemsArray.filter(
    (item) => item.options
  ) as SelectWithOption[];

  // Iterate through items with options
  for (let item of itemsWithOptions) {
    // Get options values
    const optionsValues = item.options.map((option) => option.value);
    // Check if any option of the item is selected
    const hasSelectedOptions = optionsValues.some((value) =>
      valuesArray.includes(value)
    );

    if (hasSelectedOptions) {
      // Check if all options of the item are selected
      const allOptionsSelected = optionsValues.every((value) =>
        valuesArray.includes(value)
      );

      // If all options are selected, add the item's value to the selection
      // Otherwise, remove the item's value from the selection
      if (allOptionsSelected) {
        valuesArray.push(item.value);
      } else {
        valuesArray = valuesArray.filter((val) => val !== item.value);
      }
    }
  }

  return valuesArray;
};

const formatShortDate = (date: Date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(2);
  return `${day}/${month}/${year}`;
};

export const getRenderValue = (
  values: Values,
  items: SelectItemType[],
  hasDatePicker?: boolean,
  datePickerRange?: DateRange,
  locale?: string
) => {
  if (
    typeof values === "string" &&
    values === "personalized" &&
    hasDatePicker &&
    datePickerRange
  ) {
    const [start, end] = datePickerRange;
    if (!start) return "";
    if (!end) return formatShortDate(start);
    const sameDay =
      start.getDate() === end.getDate() &&
      start.getMonth() === end.getMonth() &&
      start.getFullYear() === end.getFullYear();
    if (sameDay) return formatShortDate(start);
    return `${formatShortDate(start)} - ${formatShortDate(end)}`;
  }

  // When the date picker's apply button is clicked, the value is set to "date".
  // In this case, decide what value to render based on the range that was
  // selected, such as "January", "February - June", etc.
  if (
    typeof values === "string" &&
    values === "date" &&
    hasDatePicker &&
    datePickerRange
  ) {
    const sameYear =
      datePickerRange[0]?.getFullYear() === datePickerRange[1]?.getFullYear();
    const sameMonth =
      datePickerRange[0]?.getMonth() === datePickerRange[1]?.getMonth();
    const sameDay =
      datePickerRange[0]?.getDate() === datePickerRange[1]?.getDate();
    const months = datePickerRange.map(
      (date) => getLongMonthNames(locale ?? "es-AR")[date?.getMonth() ?? 0]
    );

    if (sameYear) {
      if (sameDay)
        return `${datePickerRange[0]?.getDate()} de ${
          months[0]
        } de ${datePickerRange[0]?.getUTCFullYear()}`;
      else {
        // if (sameMonth) return `${months[0]} ${datePickerRange[0]?.getDate()} - ${datePickerRange[1]?.getDate()}`
        if (sameMonth) return months[0];
        else return `${months[0]} - ${months[1]}`;
      }
    } else {
      return `${months[0]} ${datePickerRange[0]?.getFullYear()} - ${
        months[1]
      } ${datePickerRange[1]?.getFullYear()}`;
    }
  } else {
    // Flatten the items array to get all options on the same depth
    const flattenedItems: SelectItemType[] = flattenItemOptions(items);

    // Handle the case for multiple selections
    if (Array.isArray(values)) {
      // Filter the flattened items to only include those whose value is in the selected values
      const selectedValues = flattenedItems.filter((item) =>
        values.includes(item.value)
      );
      const allOptionSelected = selectedValues.some((item) => item.selectAll);

      if (allOptionSelected) {
        return "Todas";
      }

      // If less than 3 items are selected, concatenate their labels
      if (selectedValues.length < 3) {
        return selectedValues
          .map(
            (item, index) =>
              `${item.label}${
                selectedValues.length > 1 && index !== selectedValues.length - 1
                  ? ", "
                  : ""
              }`
          )
          .join("");
      } else {
        // If 3 or more items are selected, return a generic label
        return "Personalizado";
      }
    } else {
      // Handle single selection - find the label of the selected item or return an empty string if not found
      return flattenedItems.find((item) => item.value === values)?.label ?? "";
    }
  }
};

export const valueIsSelected = (value: string, values: Values) => {
  return Array.isArray(values)
    ? Array.from(values).includes(value)
    : value === values;
};
